"use client";

import { startOfMonth, endOfMonth } from "date-fns";
import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useRouter } from "next/navigation";

import { Card, CardContent } from "@/app/_module/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/_module/components/ui/tabs";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useAgencyQueryInvalidation } from "@/app/_module/hooks/useAgencyQueryInvalidation";
import useAgencyStatus from "@/app/_module/hooks/useAgencyStatus";
import SubscriptionGateModal, { type UserScenario } from "@/app/_module/components/SubscriptionGateModal";
import { useGetAgencyUsers, useGetSubAgencies } from "../hooks/use-hook";
import type { SubAgency, AgencyUserWithAgency } from "../types/types";

import { BarChart3 } from "lucide-react";

import ManageAgencyTable from "./agency/AgencyManagement";
import DialEfficiencyTab from "./agency/DialEfficiencyTab";
import MasterExport from "./agency/MasterExport";
import { AgencyUsersTable } from "./agency/AgencyUsersTable";
import { AgencyWideSummary } from "./agency/AgencyWideSummary";
// CreateAgencyModal import removed — the sub-agency create button lives only in the Sub Agencies tab now
import { DateRangeSelector, type CustomDateRange } from "./agency/DateRangeSelector";
import { PerformanceTrends } from "./agency/PerformanceTrends";
import { SubAgenciesContainer } from "./agency/SubAgenciesContainer";
import { SubAgencyDetails } from "./agency/SubAgencyDetails";
import { AgentDetailsView } from "./agency/AgentDetailsView";
import { TopPerformers } from "./agency/TopPerformers";
import { CarrierBreakdown } from "./agency/CarrierBreakdown";
import AgencyAnnouncements from "./agency/AgencyAnnouncements";
import { TechficientCommissionsTab } from "./agency/techficient/TechficientCommissionsTab";
import { useFeatureFlag } from "@/app/_module/hooks/useFeatureFlag";
import AgencyWideToggle from "./agency/AgencyWideToggle";
import AdminCallingToggle from "./agency/AdminCallingToggle";
import AgencyPolicyMap from "./agency/AgencyPolicyMap";
import LiveCalls from "./agency/LiveCalls";
import RoutesToggle from "./agency/RoutesToggle";
import AgencyRoutesTab from "./agency/AgencyRoutesTab";
import AgencyHierarchyTab from "./agency/AgencyHierarchyTab";
import AgencyTvDisplaySettings from "./agency/AgencyTvDisplaySettings";
import { useAgencyRouteSettings } from "../hooks/use-agency-routes";
import { Map as MapIcon, MonitorUp } from "lucide-react";

const allowedAgencyRolesSet = new Set(["owner", "admin", "agency_owner", "agency-owner"]);
const normalizeRole = (role?: string) => (role || "").trim().toLowerCase().replace(/\s+/g, "_");

// Feature flag — hides the Master Downline Export entry in Agency Center
// sidebar + content area while the feature is still in dev/QA. Flip to
// `true` when ready to expose to agency owners. Sidebar item and content
// block both gate on this.
const MASTER_EXPORT_ENABLED = true;

// URL-friendly agent identifier: use the NAME (readable, non-revealing) instead
// of the raw user UUID. Restore matches the slug against the loaded roster;
// legacy ?agentId= links keep working.
const agentSlug = (name?: string | null) =>
  (name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const AgencyStats = function () {
  const searchParams = useSearchParams();
  const router = useRouter();

  // Get initial tab from URL or default to "agencies"
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('tab') || 'agencies';
  });

  // Agency Center sidebar section
  const [agencyCenterSection, setAgencyCenterSection] = useState("announcements");

  // Get initial analytics tab from URL or default to "premium-sold"
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState(() => {
    // 'hierarchy' moved to the PRIMARY tab group (next to Agency Center), so an older
    // ?analyticsTab=hierarchy bookmark would select a panel that no longer exists here.
    const fromUrl = searchParams.get('analyticsTab');
    return fromUrl && fromUrl !== 'hierarchy' ? fromUrl : 'premium-sold';
  });

  // Opens on This Month (owner, 2026-10-10). Its own dropdown option since 2026-10-20, so the
  // dropdown reads "This Month" instead of "Custom Range". The custom range below starts on
  // this month too, so switching to Custom Range opens there.
  const [selectedDateRange, setSelectedDateRange] = useState("this-month");
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange | undefined>(() => {
    const today = new Date();
    return { from: startOfMonth(today), to: endOfMonth(today) };
  });

  const [selectedSubAgency, setSelectedSubAgency] = useState<SubAgency | null>(
    null
  );
  // Navigation stack for drilling down through nested sub-agencies
  const [subAgencyStack, setSubAgencyStack] = useState<SubAgency[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgencyUserWithAgency | null>(
    null
  );

  // Track current agency ID in state to force re-render on agency change
  const [currentAgencyId, setCurrentAgencyId] = React.useState<string | null>(
    AgencyTokenManager.getCurrentAgencyId()
  );

  const { user } = useUserContext();

  // Routes feature toggle (controls visibility of the Routes sidebar item)
  const { data: routeSettings } = useAgencyRouteSettings(currentAgencyId || '');
  const routesEnabled = routeSettings?.routesEnabled === true;

  // Auto-fallback if Routes gets disabled while selected
  useEffect(() => {
    if (agencyCenterSection === "routes" && !routesEnabled) {
      setAgencyCenterSection("settings");
    }
  }, [agencyCenterSection, routesEnabled]);

  // Fetch agency users to restore agent from URL
  // includeSubAgencies: the URL-restore below must be able to find agents from
  // SUB-agencies too — refreshing on a sub-agency agent's details bounced back
  // to the list because the root-only roster couldn't match them (2026-07-12).
  const { data: agencyUsersData, isLoading: isLoadingAgencyUsers } = useGetAgencyUsers(currentAgencyId, true);

  // Fetch sub-agencies to restore sub-agency view from URL
  const { data: subAgenciesData, isLoading: isLoadingSubAgencies } = useGetSubAgencies({
    parentAgencyId: currentAgencyId || undefined,
  });

  // Check if we're waiting for data to restore a view from URL
  const subAgencyIdFromUrl = searchParams.get('subAgencyId');
  const agentIdFromUrl = searchParams.get('agentId') || searchParams.get('agent');
  const isRestoringSubAgencyView = subAgencyIdFromUrl && isLoadingSubAgencies && !selectedSubAgency;
  const isRestoringAgentView = agentIdFromUrl && isLoadingAgencyUsers && !selectedAgent;

  // Handle tab change - update URL to persist tab selection
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    const params = new URLSearchParams(searchParams.toString());
    params.set('tab', value);
    // Clear agent view when changing tabs
    try { window.sessionStorage.removeItem('agency-dashboard:agent-id'); } catch { /* noop */ }
    params.delete('agentId');
    params.delete('agent');
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  // Handle analytics tab change - update URL to persist analytics tab selection
  const handleAnalyticsTabChange = (value: string) => {
    setActiveAnalyticsTab(value);
    const params = new URLSearchParams(searchParams.toString());
    params.set('analyticsTab', value);
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  // Sync tab state with URL on initial load and URL changes
  // Also restore agent view and sub-agency view from URL
  // NOTE: We intentionally exclude selectedAgent and selectedSubAgency from dependencies
  // to avoid race conditions when navigating (state updates before URL does)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const analyticsTabFromUrl = searchParams.get('analyticsTab');

    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }

    if (analyticsTabFromUrl && analyticsTabFromUrl !== activeAnalyticsTab) {
      setActiveAnalyticsTab(analyticsTabFromUrl);
    }
  }, [searchParams, activeTab, activeAnalyticsTab]);

  // Separate effect for restoring sub-agency and agent views from URL
  // This only runs when data loads, not when selection changes
  useEffect(() => {
    const agentIdFromUrl = searchParams.get('agentId');
    const agentSlugFromUrl = searchParams.get('agent');
    const subAgencyIdFromUrl = searchParams.get('subAgencyId');

    // Restore sub-agency view from URL if we have the data and no selection yet
    if (subAgencyIdFromUrl && subAgenciesData && !selectedSubAgency) {
      const subAgenciesList: SubAgency[] = Array.isArray(subAgenciesData)
        ? subAgenciesData
        : (subAgenciesData as any)?.items || [];
      const subAgency = subAgenciesList.find(
        (sa: SubAgency) => sa.id === subAgencyIdFromUrl
      );
      if (subAgency) {
        setSelectedSubAgency(subAgency);
      }
    }

    // Restore agent view from URL if we have the data and no selection yet
    // sessionStorage fallback: survives refresh even if the slug/roster match
    // misses (per-tab only, so no IDs leak into shareable URLs)
    let storedAgentId = typeof window !== 'undefined' ? window.sessionStorage.getItem('agency-dashboard:agent-id') : null;
    // The stash is ONLY a refresh aid — if the URL no longer references an
    // agent (user left via sidebar/other nav), it's stale: clear it and never
    // auto-open details the user didn't ask for.
    if (storedAgentId && !agentIdFromUrl && !agentSlugFromUrl) {
      try { window.sessionStorage.removeItem('agency-dashboard:agent-id'); } catch { /* noop */ }
      storedAgentId = null;
    }
    // The users endpoint returns { statusCode, data: [...] } — there is NO
    // top-level `users` key, which silently killed this restore forever
    // (agencyUsersData?.users was always undefined). Normalize all shapes.
    const roster: AgencyUserWithAgency[] =
      (agencyUsersData as any)?.users ||
      (Array.isArray((agencyUsersData as any)?.data) ? (agencyUsersData as any).data : (agencyUsersData as any)?.data?.users) ||
      [];
    if ((agentIdFromUrl || agentSlugFromUrl) && roster.length > 0 && !selectedAgent) {
      const agentUser = roster.find(
        (u: AgencyUserWithAgency) =>
          (agentIdFromUrl && u.userId === agentIdFromUrl) ||
          (agentSlugFromUrl && agentSlug(u.user?.name) === agentSlugFromUrl) ||
          (storedAgentId && u.userId === storedAgentId)
      );
      if (agentUser) {
        setSelectedAgent(agentUser);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, agencyUsersData, subAgenciesData]);

  // Listen for agency changes and update the current agency ID
  React.useEffect(() => {
    const handleAgencyChange = () => {
      const newAgencyId = AgencyTokenManager.getCurrentAgencyId();
      setCurrentAgencyId(newAgencyId);
    };

    window.addEventListener('insuracentral-agency-change', handleAgencyChange);
    return () => {
      window.removeEventListener('insuracentral-agency-change', handleAgencyChange);
    };
  }, []);

  // Invalidate queries when agency changes to prevent data duplication
  useAgencyQueryInvalidation();

  // Techficient Commissions tab — MRFG-gated via `quoteAndEnroll` feature flag.
  // Cascades down the agency tree so sub-agencies inherit. Hides the tab + its
  // pane entirely for non-MRFG agencies so they don't even see it exists.
  const { enabled: techficientEnabled } = useFeatureFlag("quoteAndEnroll");

  const contextRole = user.currentAgency?.role;
  const storedRole = AgencyTokenManager.getCurrentAgency()?.role;
  // Dashboard access is based on agency role only (owner/admin), NOT userType
  const candidates = [contextRole, storedRole];
  const hasAccess = candidates.some(c => c && allowedAgencyRolesSet.has(normalizeRole(c)));
  const lacksAccess = !hasAccess;

  // Subscription gate modal for users without access
  const [isGateOpen, setIsGateOpen] = React.useState(lacksAccess);
  const { data: agencyStatus } = useAgencyStatus();
  // agency-status has no account type for a solo user; fall back to the user
  // record before assuming Basic (a Lite trial was labelled "basic").
  const accountType = agencyStatus?.accountType || (user as any)?.user?.accountType || 'basic';
  const hasAgency = agencyStatus?.hasAgency || false;
  const isInAgency = !!user.currentAgency?.agency?.id;

  const gateScenario: UserScenario = React.useMemo(() => {
    const isFledgedTier = accountType === 'full_fledged' || accountType === 'super_agent';
    if (accountType === 'basic') {
      return isInAgency ? 'basic_in_agency' : 'basic_independent';
    } else if (isFledgedTier && isInAgency) {
      return 'full_fledged_in_agency';
    }
    return 'full_fledged_independent';
  }, [accountType, isInAgency]);

  // Re-open gate when access changes
  React.useEffect(() => {
    if (lacksAccess) setIsGateOpen(true);
  }, [lacksAccess]);

  // Convert date range string to period type or custom dates
  const getDateFilterParams = (): { period: '6-months' | '12-months' | 'year-to-date'; startDate?: string; endDate?: string } => {
    // If custom range is selected and we have valid dates, use them.
    // Normalize to full-day boundaries: presets like "Today" set from===to===now
    // (a zero-width window → $0), and calendar picks land on 00:00 for both ends
    // (so a range ending today excludes everything sold during today). Expand
    // start to 00:00:00 and end to 23:59:59.999 so the whole day(s) are inclusive.
    // This Month is a custom range over the current month.
    const range =
      selectedDateRange === 'this-month'
        ? { from: startOfMonth(new Date()), to: endOfMonth(new Date()) }
        : selectedDateRange === 'custom'
          ? customDateRange
          : undefined;
    if (range?.from && range?.to) {
      const start = new Date(range.from);
      start.setHours(0, 0, 0, 0);
      const end = new Date(range.to);
      end.setHours(23, 59, 59, 999);
      return {
        period: '6-months', // Fallback period (backend uses this if startDate/endDate not supported)
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      };
    }

    // Otherwise use preset periods
    switch (selectedDateRange) {
      case '6-months':
        return { period: '6-months' };
      case '1-year':
      case '12-months':
        return { period: '12-months' };
      case 'all-time':
        // Legacy "All Time" preset removed (Jun 2026) — it sent
        // startDate=2000-01-01 and crashed the platform service when the
        // agency-stats CTE scanned 26 years of data. Any persisted state
        // (localStorage, URL param) that still references 'all-time' now
        // silently falls back to 12-months — the largest window the
        // backend will honor.
        return { period: '12-months' };
      case 'ytd':
        return { period: 'year-to-date' };
      default:
        return { period: '6-months' };
    }
  };

  // For components that only accept period (backwards compatibility)
  const getPeriodFromDateRange = (dateRange: string): '6-months' | '12-months' | 'year-to-date' => {
    switch (dateRange) {
      case '6-months':
        return '6-months';
      case '1-year':
      case '12-months':
      case 'all-time':
        return '12-months';
      case 'ytd':
        return 'year-to-date';
      case 'custom':
        return '6-months';
      default:
        return '6-months';
    }
  };

  const handleViewSubAgency = (subAgency: SubAgency) => {
    // Push current sub-agency onto stack before navigating deeper
    if (selectedSubAgency) {
      setSubAgencyStack(prev => [...prev, selectedSubAgency]);
    }
    setSelectedSubAgency(subAgency);
    // Persist sub-agency ID in URL for refresh support
    const params = new URLSearchParams(searchParams.toString());
    params.set('subAgencyId', subAgency.id);
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  const handleBackToOverview = () => {
    if (subAgencyStack.length > 0) {
      // Go back one level - pop the parent sub-agency from the stack
      const newStack = [...subAgencyStack];
      const parentSubAgency = newStack.pop()!;
      setSubAgencyStack(newStack);
      setSelectedSubAgency(parentSubAgency);
      // Update URL to parent sub-agency
      const params = new URLSearchParams(searchParams.toString());
      params.set('subAgencyId', parentSubAgency.id);
      router.replace(`?${params.toString()}`, { scroll: false });
    } else {
      // At top level - go back to main overview
      setSelectedSubAgency(null);
      // Remove sub-agency ID from URL when going back
      const params = new URLSearchParams(searchParams.toString());
      params.delete('subAgencyId');
      router.replace(`?${params.toString()}`, { scroll: false });
    }
  };

  const handleViewAgentDetails = (agencyUser: AgencyUserWithAgency) => {
    setSelectedAgent(agencyUser);
    // Persist agent ID in URL for refresh support
    const params = new URLSearchParams(searchParams.toString());
    params.delete('agentId');
    params.set('agent', agentSlug(agencyUser.user?.name) || agencyUser.userId);
    try { window.sessionStorage.setItem('agency-dashboard:agent-id', agencyUser.userId); } catch { /* private mode */ }
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  const handleBackFromAgentDetails = () => {
    setSelectedAgent(null);
    // Remove agent params from URL when going back
    try { window.sessionStorage.removeItem('agency-dashboard:agent-id'); } catch { /* noop */ }
    const params = new URLSearchParams(searchParams.toString());
    params.delete('agentId');
    params.delete('agent');
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div data-tour="agency-header" className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            {selectedAgent
              ? "Agent Performance Details"
              : selectedSubAgency
              ? "Sub-Agency Details"
              : "Agency Performance Dashboard"}
          </h2>
          <p className="text-muted-foreground">
            {selectedAgent
              ? "Detailed view of individual agent performance metrics"
              : selectedSubAgency
              ? "Detailed view of sub-agency performance and agents"
              : "Comprehensive analytics for your life insurance agency network"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeSelector
            onRangeChange={setSelectedDateRange}
            selectedRange={selectedDateRange}
            onCustomDateChange={setCustomDateRange}
            customDateRange={customDateRange}
          />
          {/* NOTE: the "Create Sub Agency" button lives in the Sub Agencies tab
              (SubAgenciesTable) — not here. Having it in two places made it confusing
              for parent owners who didn't realize which flow they were triggering. */}
        </div>
      </div>

      {lacksAccess ? (
        <>
          <Card>
            <CardContent className="flex items-center justify-center min-h-[200px]">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-muted-foreground">Limited Access</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  You don&apos;t have permission to view agency performance. You can still create a new agency.
                </p>
              </div>
            </CardContent>
          </Card>
          <SubscriptionGateModal
            isOpen={isGateOpen}
            onClose={() => setIsGateOpen(false)}
            featureName="Agency Dashboard"
            userScenario={gateScenario}
            accountType={accountType as 'trial' | 'basic' | 'full_fledged' | 'enterprise' | 'super_agent'}
            hasAgency={hasAgency}
            currentAgencyId={user.currentAgency?.agency?.id}
          />
        </>
      ) : isRestoringSubAgencyView || isRestoringAgentView ? (
        // Show loading state while restoring view from URL
        <Card>
          <CardContent className="flex items-center justify-center min-h-[200px]">
            <div className="text-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          </CardContent>
        </Card>
      ) : selectedAgent ? (
        <AgentDetailsView
          agencyId={selectedAgent.agencyId || user.currentAgency?.agency?.id || ''}
          agencyUser={selectedAgent}
          onBack={handleBackFromAgentDetails}
          backLabel={selectedSubAgency ? selectedSubAgency.name : 'Dashboard'}
          {...getDateFilterParams()}
        />
      ) : selectedSubAgency ? (
        <SubAgencyDetails
          onBack={handleBackToOverview}
          onViewAgentDetails={handleViewAgentDetails}
          onViewSubAgency={handleViewSubAgency}
          {...getDateFilterParams()}
          subAgency={selectedSubAgency}
          parentName={subAgencyStack.length > 0 ? subAgencyStack[subAgencyStack.length - 1].name : undefined}
        />
      ) : (
        <React.Fragment>
          {/* Agency-wide summary with real data */}
          <div data-tour="agency-metrics">
            <AgencyWideSummary
              {...getDateFilterParams()}
              onOpenRoutes={
                routesEnabled
                  ? () => {
                      handleTabChange("agency-center");
                      setAgencyCenterSection("routes");
                    }
                  : undefined
              }
              onOpenAgents={() => handleTabChange("users")}
            />
          </div>

          <Tabs
            className="w-full bg-white rounded shadow-md"
            value={activeTab}
            onValueChange={handleTabChange}
          >
            <div className="flex items-center justify-between p-6 border-b" data-tour="agency-tabs">
              <TabsList className={`inline-flex w-full ${techficientEnabled ? "max-w-4xl" : "max-w-2xl"}`}>
                <TabsTrigger value="agencies" className="flex-1" data-tour="agency-tab-manage">Manage Agency</TabsTrigger>
                <TabsTrigger value="sub-agencies" className="flex-1" data-tour="agency-tab-subagencies">
                  Sub Agencies
                </TabsTrigger>
                <TabsTrigger value="users" className="flex-1" data-tour="agency-tab-agents">
                  Agency Agents
                </TabsTrigger>
                <TabsTrigger value="agency-center" className="flex-1" data-tour="agency-tab-center">
                  Agency Center
                </TabsTrigger>
                <TabsTrigger value="hierarchy" className="flex-1" data-tour="agency-tab-hierarchy">
                  Hierarchy
                </TabsTrigger>
                {techficientEnabled && (
                  <TabsTrigger value="commissions" className="flex-1" data-tour="agency-tab-commissions">
                    Commissions
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            <TabsContent className="p-6" value="sub-agencies">
              <SubAgenciesContainer
                onViewDetails={handleViewSubAgency}
                onViewAgentDetails={handleViewAgentDetails}
                parentAgencyId={user.currentAgency?.agency?.id}
                {...getDateFilterParams()}
              />
            </TabsContent>

            <TabsContent className="p-6" value="agencies">
              <ManageAgencyTable />
            </TabsContent>

            <TabsContent className="p-6" value="users">
              <div data-tour="agent-list">
                <AgencyUsersTable
                  agencyId={currentAgencyId || ''}
                  onViewAgentDetails={handleViewAgentDetails}
                  includeSubAgencies
                  {...getDateFilterParams()}
                />
              </div>
            </TabsContent>

            {techficientEnabled && (
              <TabsContent className="p-6" value="commissions">
                <TechficientCommissionsTab
                  agencyId={currentAgencyId || ''}
                  onViewAgentDetails={handleViewAgentDetails}
                />
              </TabsContent>
            )}

            <TabsContent className="p-0" value="agency-center">
              <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-[600px]">
                {/* Sidebar nav */}
                <nav className="agency-center-nav border-r border-gray-100 bg-gray-50/40 p-4 lg:p-5 flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible">
                  {[
                    { id: "announcements", label: "Announcements", icon: "megaphone" },
                    // LIVE CALLS (enabled 2026-07-25).
                    //
                    // Role set deliberately mirrors call-stream.gateway.ts's
                    // PRIVILEGED_ROLES exactly — ['owner','admin','agency_owner',
                    // 'agency-owner'] — NOT the broader REST guard. The socket is the
                    // narrowest gate in the chain: a role that passes
                    // @AgencyOwnersAndAdminOnly on /coach/start but is missing from
                    // PRIVILEGED_ROLES gets the stream created (and billed) and is then
                    // refused audio with "Not authorized for this call" — the exact
                    // failure we chased on 2026-07-25. Showing the tab only to roles
                    // that can complete the whole flow keeps those in lockstep.
                    // If you widen one, widen both.
                    ...(['owner', 'admin', 'agency_owner', 'agency-owner'].includes(normalizeRole(contextRole || storedRole))
                      ? [{ id: "live-calls", label: "Live Calls", icon: "radio" }]
                      : []),
                    ...(routesEnabled
                      ? [{ id: "routes", label: "Routes", icon: "map" }]
                      : []),
                    ...(['owner', 'admin', 'agency_owner', 'agency-owner'].includes(normalizeRole(contextRole || storedRole))
                      ? [{ id: "tv-display", label: "TV Display", icon: "tv" }]
                      : []),
                    // Master Downline Export — owners only (backend re-verifies).
                    // Sidebar entry hidden for non-owners; clicking it opens the
                    // column-picker / scope / filters wizard in the content area.
                    // Wrapped in MASTER_EXPORT_ENABLED flag while feature is in
                    // dev/QA — flip the const at the top of the file to enable.
                    ...(MASTER_EXPORT_ENABLED && ['owner', 'agency_owner'].includes(normalizeRole(contextRole || storedRole))
                      ? [{ id: "master-export", label: "Master Export", icon: "download" }]
                      : []),
                    { id: "settings", label: "Settings", icon: "cog" },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setAgencyCenterSection?.(item.id)}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                        agencyCenterSection === item.id
                          ? "bg-white text-gray-900 shadow-sm border border-gray-200/80"
                          : "text-gray-500 hover:text-gray-700 hover:bg-white/60"
                      }`}
                    >
                      {item.icon === "radio" && (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9M7.8 16.2a5.5 5.5 0 010-8.4M12 12h.01" />
                          <circle cx="12" cy="12" r="2" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.2 7.8a5.5 5.5 0 010 8.4M19.1 4.9C23 8.8 23 15.2 19.1 19.1" />
                        </svg>
                      )}
                      {item.icon === "cog" && (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                      {item.icon === "map" && (
                        <MapIcon className="w-4 h-4" />
                      )}
                      {item.icon === "tv" && (
                        <MonitorUp className="w-4 h-4" />
                      )}
                      {item.icon === "megaphone" && (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                      )}
                      {item.icon === "download" && (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                        </svg>
                      )}
                      {item.label}
                    </button>
                  ))}
                </nav>

                {/* Content area */}
                <div className="p-6 lg:p-8">
                  {agencyCenterSection === "live-calls" && (
                    <LiveCalls />
                  )}

                  {agencyCenterSection === "settings" && (
                    <div>
                      <div className="mb-6">
                        <h2 className="text-base font-semibold text-gray-900 tracking-tight">Agency Settings</h2>
                        <p className="text-sm text-gray-500 mt-0.5">Manage permissions and feature access for your agency.</p>
                      </div>
                      <div className="grid gap-3">
                        <AgencyWideToggle />
                        <AdminCallingToggle />
                        <RoutesToggle agencyId={currentAgencyId || ''} />
                      </div>
                    </div>
                  )}

                  {agencyCenterSection === "routes" && routesEnabled && (
                    <AgencyRoutesTab agencyId={currentAgencyId || ''} />
                  )}

                  {agencyCenterSection === "tv-display" && currentAgencyId && (
                    <AgencyTvDisplaySettings agencyId={currentAgencyId} />
                  )}

                  {MASTER_EXPORT_ENABLED
                    && agencyCenterSection === "master-export"
                    && ['owner', 'agency_owner'].includes(normalizeRole(contextRole || storedRole))
                    && currentAgencyId && (
                    <div>
                      <div className="ac-intro-dup mb-6">
                        <h2 className="text-base font-semibold text-gray-900 tracking-tight">Master Downline Export</h2>
                        <p className="text-sm text-gray-500 mt-0.5">Export leads from your entire downline — sub-agencies and agents&apos; personal pools — into CSV or Excel.</p>
                      </div>
                      <MasterExport sourceAgencyId={currentAgencyId} />
                    </div>
                  )}

                  {(agencyCenterSection === "announcements" || !agencyCenterSection) && (
                    <div>
                      {/* ac-intro-dup: hidden on the shell — AgencyAnnouncements
                          renders its own header right below */}
                      <div className="ac-intro-dup mb-6">
                        <h2 className="text-base font-semibold text-gray-900 tracking-tight">Announcements</h2>
                        <p className="text-sm text-gray-500 mt-0.5">Broadcast messages agency-wide.</p>
                      </div>
                      <AgencyAnnouncements />
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent className="p-6" value="hierarchy">
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Agency Hierarchy</h3>
                <p className="text-sm text-muted-foreground">
                  Agency-wide — every sub-agency, who owns it, and how the headcount and
                  production roll up
                </p>
              </div>
              <AgencyHierarchyTab
                agencyId={currentAgencyId || undefined}
                startDate={getDateFilterParams().startDate}
                endDate={getDateFilterParams().endDate}
                period={getDateFilterParams().period}
              />
            </TabsContent>
          </Tabs>

          {/* Analytics Tabs - Premium Sold, Top Performers, Trends */}
          <Tabs
            className="w-full bg-white rounded shadow-md"
            value={activeAnalyticsTab}
            onValueChange={handleAnalyticsTabChange}
          >
            <div className="flex items-center justify-between p-6 border-b" data-tour="agency-analytics-tabs">
              <TabsList className="inline-flex w-full max-w-xl">
                <TabsTrigger value="premium-sold" className="flex-1" data-tour="agency-tab-premium">
                  Premium Sold
                </TabsTrigger>
                <TabsTrigger value="top-performers" className="flex-1" data-tour="agency-tab-performers">
                  Top Performers
                </TabsTrigger>
                <TabsTrigger value="trends" className="flex-1" data-tour="agency-tab-trends">
                  Trends
                </TabsTrigger>
                <TabsTrigger value="policy-map" className="flex-1" data-tour="agency-tab-policy-map">
                  Map
                </TabsTrigger>
                <TabsTrigger value="dial-efficiency" className="flex-1" data-tour="agency-tab-dial-efficiency">
                  Dial Efficiency
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent className="p-6" value="premium-sold">
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Production by Carrier</h3>
                <p className="text-sm text-muted-foreground">Breakdown of all premium sold by carriers agency-wide</p>
              </div>
              <div className="flex flex-col lg:flex-row gap-6 items-start">
                {/* Left side: Pie Chart with carrier names below */}
                <div className="flex-shrink-0">
                  <CarrierBreakdown {...getDateFilterParams()} inTab showChartOnly />
                </div>
                {/* Right side: Data Table - fills remaining space */}
                <div className="flex-1 border rounded-lg overflow-hidden h-[500px]">
                  <CarrierBreakdown {...getDateFilterParams()} inTab showTableOnly />
                </div>
              </div>
            </TabsContent>

            <TabsContent className="p-6" value="top-performers">
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Top Performers</h3>
                <p className="text-sm text-muted-foreground">Best performing agents across all agencies you own</p>
              </div>
              <TopPerformers {...getDateFilterParams()} inTab onViewAgentDetails={handleViewAgentDetails} />
            </TabsContent>

            <TabsContent className="p-6" value="trends" data-tour="agency-analytics">
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Performance Trends</h3>
                <p className="text-sm text-muted-foreground">Monthly performance analysis across all agencies you own</p>
              </div>
              <PerformanceTrends
                agencyId={currentAgencyId || ''}
                {...getDateFilterParams()}
                inTab
              />
            </TabsContent>

            <TabsContent className="p-6" value="policy-map">
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Map</h3>
                <p className="text-sm text-muted-foreground">Geographic distribution of policies and leads across all agencies you own</p>
              </div>
              <AgencyPolicyMap agencyId={currentAgencyId || ''} {...getDateFilterParams()} />
            </TabsContent>

            <TabsContent className="p-6" value="dial-efficiency">
              <DialEfficiencyTab agencyId={currentAgencyId || ''} {...getDateFilterParams()} />
            </TabsContent>

          </Tabs>
        </React.Fragment>
      )}
    </div>
  );
};

export { allowedAgencyRolesSet as allowedAgencyRoles };
